// lib/proxy-client.ts
import { isPlasmicProxy } from './isPlasmicProxy';

let cachedFingerprint: string | null = null;
let handshakePromise: Promise<string | null> | null = null;

/**
 * Ensures we have a valid proxy fingerprint.
 * Uses a "Singleton Promise" to prevent race conditions.
 */
export async function getProxyFingerprint(): Promise<string | null> {
  // 1. If not proxy mode, return null immediately
  if (!isPlasmicProxy()) return null;

  // 2. If we already have it, return it
  if (cachedFingerprint) return cachedFingerprint;

  // 3. If a request is already in flight, wait for it (deduplication)
  if (handshakePromise) return handshakePromise;

  // 4. Otherwise, start the handshake
  console.log("[ProxyClient] Initiating Handshake...");
  handshakePromise = fetch('/api/proxy-handshake')
    .then(async (res) => {
      if (!res.ok) throw new Error('Handshake failed');
      const data = await res.json();
      return data.fingerprint as string;
    })
    .then((fingerprint) => {
      console.log("[ProxyClient] ✅ Fingerprint acquired.");
      cachedFingerprint = fingerprint;
      return fingerprint;
    })
    .catch((err) => {
      console.error("[ProxyClient] ❌ Handshake error:", err);
      return null;
    })
    .finally(() => {
      // Clear promise so retries can happen if it failed
      handshakePromise = null; 
    });

  return handshakePromise;
}

/**
 * A drop-in replacement for `fetch` that handles the handshake automatically.
 */
export async function secureFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const headers = new Headers(init?.headers);

  // Wait for the fingerprint (if needed)
  const fingerprint = await getProxyFingerprint();

  if (fingerprint) {
    headers.set('x-proxy-fingerprint', fingerprint);
  }

  return fetch(input, { ...init, headers });
}